import { X } from 'lucide-react';
import MessageRow from '@/components/comms/MessageRow';
import MessageComposer from '@/components/comms/MessageComposer';
import type { InternalCommsMessage } from '@/domain/types';
import type { Person } from '@/utils/commsHelpers';

interface Props {
  parent: InternalCommsMessage;
  replies: InternalCommsMessage[];
  peopleById: Map<string, Person>;
  people: Person[];
  onClose: () => void;
  onReply: (body: string, mentions: string[]) => void;
  onReact: (messageId: string, emoji: string) => void;
  selfPersonId: string | null;
}

export default function ThreadPane({
  parent,
  replies,
  peopleById,
  people,
  onClose,
  onReply,
  onReact,
  selfPersonId,
}: Props) {
  return (
    <aside
      className="w-80 shrink-0 border-l border-gold-dim/15 bg-ink-deep flex flex-col min-h-0"
      data-testid="comms-thread-pane"
    >
      <header className="flex items-center justify-between px-3 py-2 border-b border-gold-dim/15">
        <span className="label-small-caps text-gold-bright">Thread</span>
        <button
          type="button"
          onClick={onClose}
          className="btn-quiet p-1"
          aria-label="Close thread"
          data-testid="comms-thread-close"
        >
          <X size={12} />
        </button>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto py-2">
        <MessageRow
          message={parent}
          peopleById={peopleById}
          showThreadAffordance={false}
          onReact={onReact}
          selfPersonId={selfPersonId}
        />
        <div className="border-t border-gold-dim/15 my-2 mx-4" />
        {replies.length === 0 ? (
          <p className="font-serif text-cream-soft text-sm px-4">
            No replies yet.
          </p>
        ) : null}
        {replies.map((r) => (
          <MessageRow
            key={r.id}
            message={r}
            peopleById={peopleById}
            showThreadAffordance={false}
            onReact={onReact}
            selfPersonId={selfPersonId}
            compact
          />
        ))}
      </div>
      <MessageComposer
        onSend={onReply}
        people={people}
        placeholder="Reply in thread…"
        testid="comms-thread-composer"
        autoFocus
      />
    </aside>
  );
}
